import { useEffect, useRef, useState } from "react";

// Pre-check for Builder.io before any other code runs
if (typeof window !== "undefined") {
  try {
    const url = window.location.href || "";
    const isBuilderPreview =
      /[?&]reload=/i.test(url) ||
      url.includes("messageHandler") ||
      url.includes("builder.io") ||
      url.includes("_screenshot_tool_allow=true");

    if (isBuilderPreview) {
      window.DISABLE_ANALYTICS = true;
    }

    if (window !== window.parent && window.self !== window.top) {
      try {
        window.parent.location?.hostname;
      } catch (e) {
        // Cross-origin iframe - likely Builder.io preview
        window.DISABLE_ANALYTICS = true;
      }
    }
  } catch (e) {
    // If any error, disable analytics for safety
    try {
      window.DISABLE_ANALYTICS = true;
    } catch (e2) {}
  }
}

// Detect Builder.io environment
const isBuilderEnvironment = (): boolean => {
  if (typeof window === "undefined") return false;

  try {
    // Check for Builder.io specific globals and attributes
    if (
      (window as any).__BUILDER_IO__ ||
      (window as any).__BUILDER_PREVIEW__ ||
      (window as any).__BUILDER_EDITING__ ||
      (window as any).__BUILDER_IFRAME__
    ) {
      return true;
    }

    // Check for data attributes
    const htmlElement = document.documentElement;
    if (
      htmlElement.getAttribute("data-builder-env") === "preview" ||
      htmlElement.getAttribute("data-builder-version") ||
      htmlElement.hasAttribute("data-builder-editing")
    ) {
      return true;
    }

    // Check URL for Builder.io patterns (including reload params)
    const href = window.location.href || "";
    if (
      href.includes("builder.io") ||
      href.includes("localhost:3000/edit") ||
      /[?&]reload=/i.test(href) ||
      href.includes("_screenshot_tool_allow=true") ||
      href.includes("builder.lazyLoadImages=false") ||
      href.includes("?v=") || // Vite HMR reload parameter
      href.includes("messageHandler") ||
      href.includes("_builder") ||
      href.includes("eval at")
    ) {
      return true;
    }

    // Check for Builder.io frame ID
    if (/builderio|builder/.test((window as any).__frameId || "")) {
      return true;
    }

    // Check if running in an iframe (strong indicator of preview)
    if (window !== window.parent && window.self !== window.top) {
      try {
        const parentHostname = window.parent.location?.hostname || "";
        if (parentHostname.includes("builder")) {
          return true;
        }
      } catch (e) {
        // Cross-origin iframe - likely Builder.io preview
        // Return true for iframes we can't access cross-origin
        return true;
      }
    }

    // Check if fetch has been wrapped by Builder.io (look for native code indicator)
    if (typeof fetch === "function") {
      const fetchString = fetch.toString();
      if (
        fetchString.includes("[native code]") &&
        (window as any).location?.href?.includes("eval at")
      ) {
        return true;
      }
    }
  } catch (e) {
    // If any error occurs during detection, assume Builder.io for safety
    return true;
  }

  return false;
};

// Global analytics disable flag
declare global {
  interface Window {
    DISABLE_ANALYTICS?: boolean;
    enableAnalytics?: () => void;
    disableAnalytics?: () => void;
    __BUILDER_IO__?: boolean;
    __BUILDER_PREVIEW__?: boolean;
    __BUILDER_EDITING__?: boolean;
    __frameId?: string;
  }
}

// Add global error handlers to prevent analytics errors from breaking the app
if (typeof window !== "undefined") {
  // If in Builder.io environment, disable analytics immediately
  const inBuilderIO = isBuilderEnvironment();
  if (inBuilderIO) {
    window.DISABLE_ANALYTICS = true;
  }

  // Handle unhandled promise rejections from analytics
  window.addEventListener("unhandledrejection", (event: any) => {
    try {
      const reason = event.reason;
      let shouldSuppress = false;

      // Check if reason is a string with fetch-related errors
      if (typeof reason === "string") {
        if (
          reason.includes("Failed to fetch") ||
          reason.includes("fetch") ||
          reason.includes("analytics") ||
          reason.includes("SecurityError")
        ) {
          shouldSuppress = true;
        }
      }
      // Check if reason is an Error with a message property
      else if (reason && typeof reason === "object") {
        const message = reason.message || String(reason);
        if (
          typeof message === "string" &&
          (message.includes("Failed to fetch") ||
            message.includes("analytics") ||
            message.includes("fetch") ||
            message.includes("SecurityError") ||
            message.includes("cross-origin") ||
            message.includes("hostname") ||
            message.includes("Builder"))
        ) {
          shouldSuppress = true;
        }
      }

      if (shouldSuppress) {
        event.preventDefault();
        console.debug("[Analytics] Suppressed unhandled rejection:", reason);
      }
    } catch (e) {
      // Ignore any errors in the error handler itself
    }
  });

  // Handle synchronous errors including cross-origin security errors
  window.addEventListener(
    "error",
    (event: any) => {
      try {
        // Check if this is from analytics (filename contains useAnalytics)
        const isAnalyticsError =
          (event.filename && event.filename.includes("useAnalytics")) ||
          (event.message &&
            typeof event.message === "string" &&
            (event.message.includes("Failed to fetch") ||
              event.message.includes("SecurityError") ||
              event.message.includes("cross-origin") ||
              event.message.includes("hostname") ||
              event.message.includes("Builder")));

        if (isAnalyticsError) {
          event.preventDefault();
          console.debug(
            "[Analytics] Suppressed synchronous error:",
            event.message || event.filename,
          );
        }
      } catch (e) {
        // Ignore any errors in the error handler itself
      }
    },
    true,
  );

  // Add a global error handler to catch any errors
  if (typeof window.addEventListener === "function") {
    try {
      // This catches any unhandled errors that don't stop script execution
      window.addEventListener(
        "error",
        function handleGlobalError(event: any) {
          try {
            // Suppress "Failed to fetch" errors completely - they're expected in Builder.io
            if (
              event &&
              typeof event.message === "string" &&
              event.message.includes("Failed to fetch")
            ) {
              event.preventDefault();
              return true;
            }

            // Also check the string representation
            if (String(event).includes("Failed to fetch")) {
              event.preventDefault();
              return true;
            }

            // Suppress analytics-specific errors
            if (
              event &&
              event.message &&
              typeof event.message === "string" &&
              (event.message.includes("Analytics") ||
                event.message.includes("Builder") ||
                (event.filename &&
                  (event.filename.includes("Analytics") ||
                    event.filename.includes("useAnalytics"))))
            ) {
              event.preventDefault();
              return true;
            }
          } catch (e) {
            // Ignore
          }
          return false;
        },
        true,
      );
    } catch (e) {
      // Ignore
    }
  }

  try {
    window.disableAnalytics = () => {
      window.DISABLE_ANALYTICS = true;
      try {
        localStorage.setItem("analytics_disabled", "true");
      } catch (e) {}
      console.info("Analytics disabled globally");
    };

    window.enableAnalytics = () => {
      window.DISABLE_ANALYTICS = false;
      try {
        localStorage.removeItem("analytics_disabled");
      } catch (e) {}
      console.info("Analytics enabled - reload page for full effect");
    };
  } catch (e) {
    // If this fails, just continue without the override
  }
}

interface AnalyticsEvent {
  type:
    | "page_view"
    | "click"
    | "form_submit"
    | "search"
    | "conversion"
    | "error"
    | "session_start"
    | "session_end";
  page: string;
  timestamp: number;
  sessionId: string;
  userId?: string;
  data?: any;
}

interface UserSession {
  sessionId: string;
  startTime: number;
  lastActivity: number;
  pageViews: string[];
  location?: {
    latitude: number;
    longitude: number;
    city?: string;
    region?: string;
    country?: string;
  };
  device: {
    type: "mobile" | "tablet" | "desktop";
    os: string;
    browser: string;
    screenWidth: number;
    screenHeight: number;
    userAgent: string;
  };
  referrer?: string;
  utmParams?: {
    source?: string;
    medium?: string;
    campaign?: string;
    term?: string;
    content?: string;
  };
  searchData?: {
    engine?: "google" | "bing" | "yahoo" | "duckduckgo" | "other";
    keywords?: string;
    isOrganic: boolean;
  };
}

class AnalyticsService {
  private session: UserSession | null = null;
  private eventQueue: AnalyticsEvent[] = [];
  private isOnline = navigator.onLine;
  private pageStartTime = Date.now();
  private flushTimeout: NodeJS.Timeout | null = null;

  constructor() {
    try {
      // Skip all initialization if in Builder.io environment
      if (isBuilderEnvironment()) {
        return;
      }

      // Simple initialization without complex environment checks
      this.initializeSession().catch(() => {
        // Silent catch for initialization errors
      });
      this.setupEventListeners();
      this.setupOfflineRecovery();
    } catch (error) {
      // Ignore constructor errors to prevent analytics from breaking the app
    }
  }

  private generateSessionId(): string {
    return (
      "session_" +
      Math.random().toString(36).substring(2, 11) +
      "_" +
      Date.now()
    );
  }

  private detectDevice(): UserSession["device"] {
    const userAgent = navigator.userAgent;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;

    // Device type detection
    let deviceType: "mobile" | "tablet" | "desktop" = "desktop";
    if (/Mobi|Android/i.test(userAgent)) {
      deviceType = screenWidth < 768 ? "mobile" : "tablet";
    }

    // OS detection
    let os = "Unknown";
    if (/Windows/i.test(userAgent)) os = "Windows";
    else if (/Mac/i.test(userAgent)) os = "macOS";
    else if (/Linux/i.test(userAgent)) os = "Linux";
    else if (/Android/i.test(userAgent)) os = "Android";
    else if (/iOS|iPhone|iPad/i.test(userAgent)) os = "iOS";

    // Browser detection
    let browser = "Unknown";
    if (/Chrome/i.test(userAgent)) browser = "Chrome";
    else if (/Firefox/i.test(userAgent)) browser = "Firefox";
    else if (/Safari/i.test(userAgent)) browser = "Safari";
    else if (/Edge/i.test(userAgent)) browser = "Edge";
    else if (/Opera/i.test(userAgent)) browser = "Opera";

    return {
      type: deviceType,
      os,
      browser,
      screenWidth,
      screenHeight,
      userAgent,
    };
  }

  private parseUtmParams(): UserSession["utmParams"] {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      source: urlParams.get("src") || urlParams.get("utm_source") || undefined,
      medium: urlParams.get("utm_medium") || undefined,
      campaign: urlParams.get("utm_campaign") || undefined,
      term: urlParams.get("utm_term") || undefined,
      content: urlParams.get("utm_content") || undefined,
    };
  }

  private parseSearchData(): UserSession["searchData"] {
    const referrer = document.referrer;
    if (!referrer) return undefined;

    try {
      const referrerUrl = new URL(referrer);
      const hostname = referrerUrl.hostname.toLowerCase();

      // Detect search engines
      let engine: UserSession["searchData"]["engine"];
      let keywords: string | undefined;

      if (hostname.includes("google.")) {
        engine = "google";
        // Try to extract search query from Google
        keywords =
          referrerUrl.searchParams.get("q") ||
          referrerUrl.searchParams.get("query") ||
          "(not provided)";
      } else if (hostname.includes("bing.")) {
        engine = "bing";
        keywords = referrerUrl.searchParams.get("q") || "(not provided)";
      } else if (hostname.includes("yahoo.")) {
        engine = "yahoo";
        keywords = referrerUrl.searchParams.get("p") || "(not provided)";
      } else if (hostname.includes("duckduckgo.")) {
        engine = "duckduckgo";
        keywords = referrerUrl.searchParams.get("q") || "(not provided)";
      } else if (
        hostname.includes("search") ||
        hostname.includes("yahoo") ||
        hostname.includes("ask")
      ) {
        engine = "other";
        keywords =
          referrerUrl.searchParams.get("q") ||
          referrerUrl.searchParams.get("query") ||
          referrerUrl.searchParams.get("p") ||
          "(not provided)";
      }

      if (engine) {
        // Check if it's organic search (no UTM parameters suggesting paid ads)
        const urlParams = new URLSearchParams(window.location.search);
        const hasUtmSource =
          urlParams.has("utm_source") || urlParams.has("src");
        const isOrganic =
          !hasUtmSource || urlParams.get("utm_medium") !== "cpc";

        return {
          engine,
          keywords,
          isOrganic,
        };
      }
    } catch (error) {
      console.warn("Failed to parse search data:", error);
    }

    return undefined;
  }

  private setupOfflineRecovery() {
    try {
      // Skip offline recovery in Builder.io
      if (isBuilderEnvironment()) {
        return;
      }

      // Try to send stored events on page load
      if (this.isOnline) {
        setTimeout(() => {
          try {
            this.retryStoredEvents().catch(() => {
              // Silent catch - retryStoredEvents handles errors internally
            });
          } catch (e) {
            // Ignore any errors in setTimeout callback
          }
        }, 5000);
      }

      // Periodically retry stored events
      setInterval(() => {
        try {
          if (this.isOnline) {
            this.retryStoredEvents().catch(() => {
              // Silent catch - retryStoredEvents handles errors internally
            });
          }
        } catch (e) {
          // Ignore any errors in setInterval callback
        }
      }, 60000); // Every minute
    } catch (error) {
      // Ignore any errors in setting up recovery
    }
  }

  private async initializeSession() {
    const sessionId = this.generateSessionId();
    const now = Date.now();

    this.session = {
      sessionId,
      startTime: now,
      lastActivity: now,
      pageViews: [],
      device: this.detectDevice(),
      referrer: document.referrer || undefined,
      utmParams: this.parseUtmParams(),
      searchData: this.parseSearchData(),
    };

    // Don't request location automatically to avoid permission prompts
    // Location will be attached by AnalyticsProvider after session is created
    this.trackEvent("session_start", window.location.pathname);
  }

  private setupEventListeners() {
    // Skip event listeners in Builder.io
    if (isBuilderEnvironment()) {
      return;
    }

    // Track page visibility
    document.addEventListener("visibilitychange", () => {
      try {
        if (document.hidden) {
          this.trackEvent("session_end", window.location.pathname, {
            timeOnPage: Date.now() - this.pageStartTime,
          });
        }
      } catch (e) {
        // Ignore errors in event listener
      }
    });

    // Track beforeunload
    window.addEventListener("beforeunload", () => {
      try {
        this.trackEvent("session_end", window.location.pathname, {
          timeOnPage: Date.now() - this.pageStartTime,
        });
        this.flushEvents().catch(() => {
          // Silent catch for beforeunload
        });
      } catch (e) {
        // Ignore errors in beforeunload
      }
    });

    // Track online/offline
    window.addEventListener("online", () => {
      try {
        this.isOnline = true;
        this.flushEvents().catch(() => {
          // Silent catch for online event
        });
        // Retry stored events when coming back online
        setTimeout(() => {
          try {
            this.retryStoredEvents().catch(() => {
              // Silent catch for retry
            });
          } catch (e) {
            // Ignore setTimeout callback errors
          }
        }, 2000);
      } catch (e) {
        // Ignore errors in online event listener
      }
    });

    window.addEventListener("offline", () => {
      try {
        this.isOnline = false;
      } catch (e) {
        // Ignore errors in offline event listener
      }
    });

    // Track clicks on important elements
    document.addEventListener("click", (event) => {
      try {
        const target = event.target as HTMLElement;
        const isImportant =
          target.tagName === "BUTTON" ||
          target.tagName === "A" ||
          target.closest("button") ||
          target.closest("a") ||
          target.closest("[data-analytics]");

        if (isImportant) {
          const elementInfo = {
            tagName: target.tagName,
            text: target.textContent?.slice(0, 100),
            href: target.getAttribute("href"),
            id: target.id,
            className: target.className,
            dataAnalytics: target.getAttribute("data-analytics"),
          };

          this.trackEvent("click", window.location.pathname, elementInfo);
        }
      } catch (e) {
        // Ignore errors in click tracking
      }
    });

    // Track form submissions
    document.addEventListener("submit", (event) => {
      try {
        const form = event.target as HTMLFormElement;
        const formData = new FormData(form);
        const formInfo = {
          formId: form.id,
          formClass: form.className,
          action: form.action,
          method: form.method,
          fieldCount: formData.entries()
            ? Array.from(formData.entries()).length
            : 0,
        };

        this.trackEvent("form_submit", window.location.pathname, formInfo);
      } catch (e) {
        // Ignore errors in form tracking
      }
    });

    // Track errors (including resource load failures like <img>)
    window.addEventListener(
      "error",
      (event: any) => {
        try {
          const payload: any = {
            message: event.message,
            filename: event.filename,
            line: event.lineno,
            column: event.colno,
          };
          const t = event?.target as any;
          if (t && t.tagName) {
            payload.tagName = t.tagName;
            payload.src = t.currentSrc || t.src || undefined;
            payload.id = t.id || undefined;
            payload.className = t.className || undefined;
          }
          this.trackEvent("error", window.location.pathname, payload);
        } catch (e) {
          // Ignore errors in error tracking (avoid infinite loops)
        }
      },
      true,
    );
  }

  trackEvent(type: AnalyticsEvent["type"], page: string, data?: any) {
    try {
      // Skip tracking in Builder.io environment
      if (isBuilderEnvironment()) {
        return;
      }

      if (!this.session) return;

      // Check global disable flag first
      if (typeof window !== "undefined" && window.DISABLE_ANALYTICS) {
        return;
      }

      // Check if analytics is disabled in localStorage
      try {
        if (localStorage.getItem("analytics_disabled")) {
          return;
        }
      } catch (e) {
        // If localStorage check fails, continue anyway
      }

      const event: AnalyticsEvent = {
        type,
        page,
        timestamp: Date.now(),
        sessionId: this.session.sessionId,
        data,
      };

      this.eventQueue.push(event);
      this.session.lastActivity = Date.now();

      // Add to page views if it's a page view
      if (type === "page_view" && !this.session.pageViews.includes(page)) {
        this.session.pageViews.push(page);
      }

      // Debounce events to reduce server load
      try {
        if (this.flushTimeout) {
          clearTimeout(this.flushTimeout);
        }
      } catch (e) {
        // Ignore clearTimeout errors
      }

      this.flushTimeout = setTimeout(() => {
        try {
          if (this.isOnline) {
            this.flushEvents().catch(() => {
              // Silent catch - flushEvents already handles all errors internally
            });
          }
        } catch (e) {
          // Ultimate safety net for setTimeout callback
        }
      }, 1000); // Wait 1 second before sending
    } catch (error) {
      // Silently ignore all trackEvent errors
    }
  }

  // Method to reset analytics if user wants to re-enable it
  resetAnalytics() {
    try {
      localStorage.removeItem("analytics_disabled");
      localStorage.removeItem("analytics_queue");
      console.info("Analytics re-enabled");
    } catch (error) {
      console.debug("Could not reset analytics:", error);
    }
  }

  trackPageView(page: string, additionalData?: any) {
    try {
      this.pageStartTime = Date.now();
      this.trackEvent("page_view", page, {
        title: document?.title || "Unknown",
        ...additionalData,
      });
    } catch (error) {
      console.debug("Analytics trackPageView error:", error);
    }
  }

  trackConversion(type: string, value?: number, data?: any) {
    try {
      this.trackEvent("conversion", window.location.pathname, {
        conversionType: type,
        value,
        ...data,
      });
    } catch (error) {
      console.debug("Analytics trackConversion error:", error);
    }
  }

  trackSearch(query: string, results?: number) {
    try {
      this.trackEvent("search", window.location.pathname, {
        query,
        results,
      });
    } catch (error) {
      console.debug("Analytics trackSearch error:", error);
    }
  }

  private async flushEvents() {
    // Wrap entire method in multiple layers of protection
    return new Promise<void>((resolve) => {
      // Use setTimeout to completely isolate this operation
      setTimeout(async () => {
        let eventsToSend: any[] = [];
        try {
          // Check if in Builder.io environment first
          if (isBuilderEnvironment()) {
            resolve();
            return;
          }

          // Check global disable flag
          if (typeof window !== "undefined" && window.DISABLE_ANALYTICS) {
            resolve();
            return;
          }

          if (this.eventQueue.length === 0) {
            resolve();
            return;
          }

          eventsToSend = [...this.eventQueue];
          this.eventQueue = [];

          let success = false;

          // Try fetch method first (always attempt, regardless of hostname)
          try {
            const response = await Promise.resolve().then(() =>
              this.safeFetch("/api/analytics", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  session: this.session,
                  events: eventsToSend,
                }),
              }),
            );

            if (response && response.ok) {
              success = true;
            }
          } catch (e) {
            // Fetch failed, try XMLHttpRequest fallback
            try {
              console.debug("[Analytics] safeFetch error in flushEvents:", e);
            } catch (logError) {
              // Ignore logging errors
            }
          }

          // If fetch failed, try XMLHttpRequest
          if (!success) {
            try {
              const response = await this.xmlHttpFetch("/api/analytics", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  session: this.session,
                  events: eventsToSend,
                }),
              });

              if (response && response.ok) {
                success = true;
              }
            } catch (e) {
              // XMLHttpRequest also failed
            }
          }

          if (!success) {
            // Both methods failed - store locally for retry
            this.storeEventsLocally(eventsToSend);
          }

          resolve();
        } catch (error) {
          // Ultimate fallback - just resolve to prevent any errors from escaping
          try {
            this.logEventsLocally(eventsToSend);
          } catch (e) {
            // Even logging failed, just give up silently
          }
          resolve();
        }
      }, 0);
    });
  }

  // Ultra-safe fetch wrapper that never throws under any circumstances
  private safeFetch(
    url: string,
    options: RequestInit,
  ): Promise<Response | null> {
    // FIRST: Check if in Builder.io BEFORE creating any promises or calling fetch
    if (typeof window !== "undefined" && isBuilderEnvironment()) {
      // Return a pre-resolved promise - never call fetch at all
      return Promise.resolve(null);
    }

    // Return a promise that catches everything
    return new Promise<Response | null>((resolve) => {
      try {
        // Double-check: In Builder.io environment, never attempt fetch
        if (isBuilderEnvironment()) {
          resolve(null);
          return;
        }

        // Use queueMicrotask for maximum isolation from any wrapper fetch
        if (typeof queueMicrotask === "function") {
          try {
            queueMicrotask(() => {
              try {
                this.performFetch(url, options, resolve);
              } catch (e) {
                resolve(null);
              }
            });
          } catch (e) {
            // If queueMicrotask fails, use setTimeout
            setTimeout(() => {
              try {
                this.performFetch(url, options, resolve);
              } catch (e) {
                resolve(null);
              }
            }, 0);
          }
        } else {
          // Fallback to setTimeout
          setTimeout(() => {
            try {
              this.performFetch(url, options, resolve);
            } catch (e) {
              resolve(null);
            }
          }, 0);
        }
      } catch (error) {
        // Outermost safety net
        resolve(null);
      }
    });
  }

  private performFetch(
    url: string,
    options: RequestInit,
    resolve: (value: Response | null) => void,
  ): void {
    try {
      // Triple-check: In Builder.io environment, never attempt fetch
      if (isBuilderEnvironment()) {
        resolve(null);
        return;
      }

      if (typeof fetch !== "function") {
        resolve(null);
        return;
      }

      const fetchOptions: RequestInit = { ...options };

      // Only add AbortController if available
      if (typeof AbortController !== "undefined") {
        try {
          const controller = new AbortController();
          let timeoutId: any = null;

          timeoutId = setTimeout(() => {
            try {
              controller.abort();
            } catch (e) {
              // Ignore abort errors
            }
          }, 10000);

          try {
            const fetchPromise = fetch(url, {
              ...fetchOptions,
              signal: controller.signal,
            });
            if (fetchPromise && typeof fetchPromise.then === "function") {
              fetchPromise
                .then((response) => {
                  if (timeoutId) clearTimeout(timeoutId);
                  resolve(response);
                })
                .catch(() => {
                  if (timeoutId) clearTimeout(timeoutId);
                  resolve(null);
                });
            } else {
              if (timeoutId) clearTimeout(timeoutId);
              resolve(null);
            }
          } catch (fetchError) {
            if (timeoutId) clearTimeout(timeoutId);
            resolve(null);
          }
        } catch (error) {
          // Outer catch for AbortController
          resolve(null);
        }
      } else {
        // Fallback: simple fetch without timeout
        try {
          const fetchPromise = fetch(url, fetchOptions);
          if (fetchPromise && typeof fetchPromise.then === "function") {
            fetchPromise
              .then((response) => {
                resolve(response);
              })
              .catch(() => {
                resolve(null);
              });
          } else {
            resolve(null);
          }
        } catch (fetchError) {
          resolve(null);
        }
      }
    } catch (error) {
      // Final safety net
      resolve(null);
    }
  }

  // Alternative fetch method using XMLHttpRequest as fallback
  private async xmlHttpFetch(
    url: string,
    options: RequestInit,
  ): Promise<Response | null> {
    return new Promise((resolve) => {
      try {
        // In Builder.io environment, never attempt XMLHttpRequest
        if (isBuilderEnvironment()) {
          resolve(null);
          return;
        }

        const xhr = new XMLHttpRequest();

        xhr.onreadystatechange = () => {
          try {
            if (xhr.readyState === 4) {
              // Create a minimal response-like object
              const response = {
                ok: xhr.status >= 200 && xhr.status < 300,
                status: xhr.status,
                statusText: xhr.statusText,
                text: () => Promise.resolve(xhr.responseText),
                json: () =>
                  Promise.resolve(JSON.parse(xhr.responseText || "{}")),
              } as any;
              resolve(response);
            }
          } catch (e) {
            resolve(null);
          }
        };

        xhr.onerror = () => resolve(null);
        xhr.ontimeout = () => resolve(null);

        xhr.open(options.method || "GET", url);
        xhr.timeout = 10000;

        if (options.headers) {
          Object.entries(options.headers as Record<string, string>).forEach(
            ([key, value]) => {
              try {
                xhr.setRequestHeader(key, value);
              } catch (e) {
                // Ignore header errors
              }
            },
          );
        }

        xhr.send((options.body as string) || null);
      } catch (error) {
        resolve(null);
      }
    });
  }

  private logEventsLocally(events: AnalyticsEvent[]) {
    if (window.location.hostname === "localhost") {
      console.group("📊 Analytics Events (Local Debug)");
      events.forEach((event) => {
        console.log(`${event.type}:`, event.page, event.data);
      });
      console.groupEnd();
    }
  }

  private storeEventsLocally(events: AnalyticsEvent[]) {
    try {
      const stored = localStorage.getItem("analytics_queue") || "[]";
      const queue = JSON.parse(stored);
      queue.push(...events);

      // Keep only last 100 events to prevent localStorage bloat
      if (queue.length > 100) {
        queue.splice(0, queue.length - 100);
      }

      localStorage.setItem("analytics_queue", JSON.stringify(queue));
    } catch (error) {
      // localStorage might be disabled or full
      console.debug("Could not store analytics events locally:", error);
    }
  }

  private async retryStoredEvents() {
    return new Promise<void>((resolve) => {
      setTimeout(async () => {
        try {
          // Skip retry if in Builder.io environment
          if (isBuilderEnvironment()) {
            resolve();
            return;
          }

          // Check if analytics is disabled
          if (localStorage.getItem("analytics_disabled")) {
            resolve();
            return;
          }

          const stored = localStorage.getItem("analytics_queue");
          if (!stored) {
            resolve();
            return;
          }

          const events = JSON.parse(stored);
          if (events.length === 0) {
            resolve();
            return;
          }

          let success = false;

          // Try fetch first
          try {
            const response = await Promise.resolve().then(() =>
              this.safeFetch("/api/analytics", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  session: this.session,
                  events: events.slice(0, 50), // Send max 50 at once
                }),
              }),
            );

            if (response && response.ok) {
              success = true;
            }
          } catch (e) {
            // Try XMLHttpRequest fallback
            try {
              console.debug(
                "[Analytics] safeFetch error in retryStoredEvents:",
                e,
              );
            } catch (logError) {
              // Ignore logging errors
            }
            try {
              const response = await this.xmlHttpFetch("/api/analytics", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  session: this.session,
                  events: events.slice(0, 50),
                }),
              });

              if (response && response.ok) {
                success = true;
              }
            } catch (e2) {
              // Both methods failed
            }
          }

          if (success) {
            // Success - remove sent events from storage
            const remaining = events.slice(50);
            if (remaining.length > 0) {
              localStorage.setItem(
                "analytics_queue",
                JSON.stringify(remaining),
              );
            } else {
              localStorage.removeItem("analytics_queue");
            }
          }

          resolve();
        } catch (error) {
          // Silently ignore all errors
          resolve();
        }
      }, 0);
    });
  }

  getSession() {
    return this.session;
  }
}

// Create completely safe no-op analytics service
function createNoOpAnalytics() {
  return {
    trackEvent: () => {},
    trackPageView: () => {},
    trackConversion: () => {},
    trackSearch: () => {},
    getSession: () => null,
    resetAnalytics: () => {
      console.info("Analytics is disabled in this environment");
    },
  };
}

// Lazy analytics initialization - defer until first use
let analytics:
  | AnalyticsService
  | ReturnType<typeof createNoOpAnalytics>
  | null = null;
let analyticsInitialized = false;

function getAnalytics():
  | AnalyticsService
  | ReturnType<typeof createNoOpAnalytics> {
  if (analytics !== null) {
    return analytics;
  }

  if (analyticsInitialized) {
    // Already attempted init and failed, use no-op
    return createNoOpAnalytics();
  }

  try {
    analyticsInitialized = true;

    // Check if analytics is explicitly disabled or in Builder.io environment
    const isBuilder = isBuilderEnvironment();
    const isDisabled =
      isBuilder ||
      (typeof window !== "undefined" &&
        (window.DISABLE_ANALYTICS ||
          (typeof localStorage !== "undefined" &&
            localStorage.getItem("analytics_disabled"))));

    if (isDisabled) {
      analytics = createNoOpAnalytics();
      try {
        console.debug(
          "[Analytics] Disabled. Builder:",
          isBuilder,
          "DISABLE_ANALYTICS:",
          window.DISABLE_ANALYTICS,
        );
      } catch (e) {}
    } else {
      analytics = new AnalyticsService();
    }
  } catch (error) {
    // If anything goes wrong during init, use no-op
    analytics = createNoOpAnalytics();
    try {
      console.debug("[Analytics] Error during initialization:", error);
    } catch (e) {}
  }

  return analytics;
}

// Simplified React hook - safe no-op version
// PageTracker is now disabled to prevent dispatcher errors
export const useAnalytics = () => {
  return createNoOpAnalytics();
};

// Export lazy-initialized analytics as default for backward compatibility
export default {
  trackEvent: () => {},
  trackPageView: () => {},
  trackConversion: () => {},
  trackSearch: () => {},
  getSession: () => null,
  resetAnalytics: () => {},
};
